import React, { FC } from 'react';
import { Box, useBreakpointValue, useTheme } from '@chakra-ui/react';
import { motion } from 'motion/react';
import Button from 'ts/common/components/gallery/Button';
import CoverPhoto from 'ts/common/components/gallery/CoverPhoto';
import GalleryHeading from 'ts/common/components/gallery/GalleryHeading';
import StudioHeading from 'ts/common/components/gallery/StudioHeading';
import StudioLogo from 'ts/common/components/gallery/StudioLogo';
import { ILayoutProps } from './BaseLayout';
import { blockLeftAnimations, getAnimationProps } from './animations';

const BlockLeftLayout: FC<ILayoutProps> = ({
    galleryTitle,
    brandName,
    coverPhoto,
    coverPhotoUrl,
    eventBrandLogo,
    logoAltText,
    buttonText,
    buttonTextLarge,
    colorScheme,
    onClick,
    enableAnimations = true
}) => {
    const theme = useTheme();
    const galleryHeadingSize =
        useBreakpointValue({ base: 'sm' as const, lg: 'md' as const }) ?? 'sm';
    const studioLogoSize = useBreakpointValue({ base: 'md' as const, lg: 'lg' as const });

    return (
        <Box position="absolute" width="full" height="full" onClick={onClick}>
            <Box
                position="relative"
                width="full"
                height="full"
                overflow="hidden"
                background={colorScheme === 'dark' ? theme.colors.gray[700] : theme.colors.white}
            >
                {/* Desktop: Cover image positioned left side, larger size */}
                <motion.div
                    style={{
                        position: 'absolute',
                        // Enforce portrait aspect ratio on desktop
                        height: useBreakpointValue({ base: '100%', lg: '75%' }) || '75%',
                        width: useBreakpointValue({ base: '100%', lg: 'auto' }) || 'auto',
                        aspectRatio:
                            useBreakpointValue({ base: undefined, lg: '3 / 4' }) || '3 / 4',
                        left: useBreakpointValue({ base: '0', lg: '5%' }) || '5%',
                        top: useBreakpointValue({ base: '0', lg: '12%' }) || '12%',
                        display: useBreakpointValue({ base: 'none', lg: 'block' }) || 'block'
                    }}
                    {...getAnimationProps(blockLeftAnimations.coverImage, enableAnimations)}
                >
                    <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                </motion.div>

                {/* Mobile: Vertical layout - content at top, cover image in middle, logo at bottom */}
                <Box
                    display={{ base: 'flex', lg: 'none' }}
                    position="absolute"
                    width="full"
                    height="full"
                    flexDirection="column"
                    padding={6}
                >
                    {/* Top: Content section */}
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={8}
                        alignItems="flex-start"
                        width="full"
                        marginBottom={6}
                    >
                        <Box
                            display="flex"
                            flexDirection="column"
                            gap={1}
                            alignItems="flex-start"
                            width="full"
                        >
                            <motion.div
                                {...getAnimationProps(
                                    blockLeftAnimations.galleryName,
                                    enableAnimations
                                )}
                            >
                                <GalleryHeading
                                    size={galleryHeadingSize}
                                    text={galleryTitle}
                                    variant="primary"
                                />
                            </motion.div>
                            {!eventBrandLogo && (
                                <motion.div
                                    {...getAnimationProps(
                                        blockLeftAnimations.studioName,
                                        enableAnimations
                                    )}
                                >
                                    <StudioHeading text={brandName} variant="primary" />
                                </motion.div>
                            )}
                        </Box>
                        <motion.div
                            {...getAnimationProps(blockLeftAnimations.viewBtn, enableAnimations)}
                        >
                            <Button
                                text={buttonText}
                                textLarge={buttonTextLarge}
                                variant="outline primary"
                                size="lg"
                                paddingX={5}
                                paddingY={3}
                            />
                        </motion.div>
                    </Box>

                    {/* Middle: Cover image */}
                    <motion.div
                        style={{
                            flex: 1,
                            width: '100%',
                            aspectRatio: '3/4',
                            overflow: 'hidden',
                            marginBottom: eventBrandLogo ? '20px' : '0'
                        }}
                        {...getAnimationProps(blockLeftAnimations.coverImage, enableAnimations)}
                    >
                        <CoverPhoto coverPhoto={coverPhoto} coverPhotoUrl={coverPhotoUrl} />
                    </motion.div>

                    {/* Bottom: Brand logo when present */}
                    {eventBrandLogo && (
                        <Box
                            display="flex"
                            justifyContent="flex-start"
                            alignItems="center"
                            paddingBottom={4}
                        >
                            <motion.div
                                {...getAnimationProps(
                                    blockLeftAnimations.studioName,
                                    enableAnimations
                                )}
                            >
                                <StudioLogo
                                    logo={eventBrandLogo}
                                    logoAltText={logoAltText}
                                    logoSize={studioLogoSize}
                                />
                            </motion.div>
                        </Box>
                    )}
                </Box>

                {/* Desktop: Brand logo at top when present */}
                {eventBrandLogo && (
                    <Box
                        display={{ base: 'none', lg: 'flex' }}
                        position="absolute"
                        right="5%"
                        top="15%"
                        width="40%"
                        justifyContent="flex-start"
                        alignItems="flex-start"
                    >
                        <motion.div
                            {...getAnimationProps(blockLeftAnimations.studioName, enableAnimations)}
                        >
                            <StudioLogo
                                logo={eventBrandLogo}
                                logoAltText={logoAltText}
                                logoSize={studioLogoSize}
                            />
                        </motion.div>
                    </Box>
                )}

                {/* Desktop: Content positioned to the right of cover image (towards bottom) */}
                <Box
                    display={{ base: 'none', lg: 'flex' }}
                    position="absolute"
                    right="5%"
                    bottom="20%"
                    width="40%"
                    flexDirection="column"
                    gap={8}
                    alignItems="flex-start"
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1}
                        alignItems="flex-start"
                        width="full"
                    >
                        <motion.div
                            {...getAnimationProps(
                                blockLeftAnimations.galleryName,
                                enableAnimations
                            )}
                        >
                            <GalleryHeading
                                size={galleryHeadingSize}
                                text={galleryTitle}
                                variant="primary"
                            />
                        </motion.div>
                        {!eventBrandLogo && (
                            <motion.div
                                {...getAnimationProps(
                                    blockLeftAnimations.studioName,
                                    enableAnimations
                                )}
                            >
                                <StudioHeading text={brandName} variant="primary" />
                            </motion.div>
                        )}
                    </Box>
                    <motion.div
                        {...getAnimationProps(blockLeftAnimations.viewBtn, enableAnimations)}
                    >
                        <Button
                            text={buttonText}
                            textLarge={buttonTextLarge}
                            variant="outline primary"
                            size="md"
                        />
                    </motion.div>
                </Box>
            </Box>
        </Box>
    );
};

export default BlockLeftLayout;
